import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBuilding,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaSearch,
  FaSyncAlt,
  FaUsers,
  FaChalkboardTeacher,
} from "react-icons/fa";
import { getClubs } from "../../../services/clubServices";
import { getDojos } from "../../../services/dojoServices";
import { getAllGroupsWithDetails } from "../../../services/groupServices";
import { getAdherentsByGroup } from "../../../services/adherentServices";
import useScrollToTop from "../../../hooks/useScollToTop";

import StatsCards from "./components/StatsCards";
import ClubsGrid from "./components/ClubsGrid";
import ClubDetailPanel from "./components/ClubDetailPanel";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getTeacherKey(teacher) {
  return (
    teacher?.id ||
    teacher?.user_id ||
    teacher?.email ||
    `${teacher?.prenom || ""}-${teacher?.nom || ""}`
  );
}

function uniqueByKey(items, getKey) {
  return Array.from(
    new Map((items || []).filter(Boolean).map((item) => [getKey(item), item])).values()
  );
}

const GroupesAdherentsAdmin = () => {
  useScrollToTop();

  const [clubs, setClubs] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);

  const [loading, setLoading] = useState(true);

  const [selectedClubId, setSelectedClubId] = useState(null);
  const [clubSearch, setClubSearch] = useState("");
  const [selectedDepartement, setSelectedDepartement] = useState("");
  const [sortBy, setSortBy] = useState("name");

  const [adherentsByGroup, setAdherentsByGroup] = useState({});
  const [loadingAdherents, setLoadingAdherents] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [clubsData, dojosData, groupsData] = await Promise.all([
        getClubs(),
        getDojos(),
        getAllGroupsWithDetails(),
      ]);

      setClubs(Array.isArray(clubsData) ? clubsData : []);
      setDojos(Array.isArray(dojosData) ? dojosData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (error) {
      console.error("Erreur chargement groupes/clubs/dojos :", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadAdherentsForGroup = useCallback(
    async (groupId) => {
      if (adherentsByGroup[groupId]) return adherentsByGroup[groupId];

      try {
        setLoadingAdherents((prev) => ({ ...prev, [groupId]: true }));
        const adherents = await getAdherentsByGroup(groupId);
        const safeAdherents = Array.isArray(adherents) ? adherents : [];

        setAdherentsByGroup((prev) => ({
          ...prev,
          [groupId]: safeAdherents,
        }));

        return safeAdherents;
      } catch (error) {
        console.error("Erreur chargement adhérents :", error);
        setAdherentsByGroup((prev) => ({
          ...prev,
          [groupId]: [],
        }));
        return [];
      } finally {
        setLoadingAdherents((prev) => ({ ...prev, [groupId]: false }));
      }
    },
    [adherentsByGroup]
  );

  const clubsEnriched = useMemo(() => {
    return clubs.map((club) => {
      const clubDojos = dojos.filter(
        (dojo) => String(dojo.club_id) === String(club.id)
      );

      const clubGroups = groups.filter(
        (group) =>
          String(group?.club?.id || group?.club_id) === String(club.id)
      );

      const dojosDetailed = clubDojos
        .map((dojo) => {
          const dojoGroups = clubGroups.filter(
            (group) =>
              String(group?.dojo?.id || group?.dojo_id) === String(dojo.id)
          );

          const totalAdherents = dojoGroups.reduce(
            (sum, group) => sum + Number(group?.adherentsCount || 0),
            0
          );

          const dojoTeachers = uniqueByKey(
            dojoGroups.flatMap((group) => group?.enseignants || []),
            getTeacherKey
          );

          return {
            ...dojo,
            groups: dojoGroups.sort((a, b) =>
              String(a?.nom_groupe || "").localeCompare(
                String(b?.nom_groupe || ""),
                "fr"
              )
            ),
            totalGroups: dojoGroups.length,
            totalAdherents,
            teachersCount: dojoTeachers.length,
          };
        })
        .sort((a, b) =>
          String(a?.nom_dojo || "").localeCompare(String(b?.nom_dojo || ""), "fr")
        );

      const uniqueTeachers = uniqueByKey(
        clubGroups.flatMap((group) => group?.enseignants || []),
        getTeacherKey
      );

      return {
        ...club,
        dojosDetailed,
        totalDojos: dojosDetailed.length,
        totalGroups: clubGroups.length,
        totalAdherents: clubGroups.reduce(
          (sum, group) => sum + Number(group?.adherentsCount || 0),
          0
        ),
        teachersCount: uniqueTeachers.length,
      };
    });
  }, [clubs, dojos, groups]);

  const selectedClub = useMemo(() => {
    return clubsEnriched.find(
      (club) => String(club.id) === String(selectedClubId)
    );
  }, [clubsEnriched, selectedClubId]);

  const departementOptions = useMemo(() => {
    return [...new Set(clubsEnriched.map((club) => club.departement_club).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), "fr"));
  }, [clubsEnriched]);

  const filteredClubs = useMemo(() => {
    const term = clubSearch.trim().toLowerCase();

    const out = clubsEnriched.filter((club) => {
      const text = `
        ${club.nom_club || ""}
        ${club.departement_club || ""}
        ${club.adresse_club || ""}
        ${club.user?.nom || ""}
        ${club.user?.prenom || ""}
      `.toLowerCase();

      const matchesSearch = !term || text.includes(term);
      const matchesDepartement = selectedDepartement
        ? String(club.departement_club) === String(selectedDepartement)
        : true;

      return matchesSearch && matchesDepartement;
    });

    out.sort((a, b) => {
      switch (sortBy) {
        case "groups_desc":
          return (b.totalGroups || 0) - (a.totalGroups || 0);
        case "adherents_desc":
          return (b.totalAdherents || 0) - (a.totalAdherents || 0);
        case "dojos_desc":
          return (b.totalDojos || 0) - (a.totalDojos || 0);
        case "departement":
          return String(a.departement_club || "").localeCompare(
            String(b.departement_club || ""),
            "fr"
          );
        case "name":
        default:
          return String(a.nom_club || "").localeCompare(
            String(b.nom_club || ""),
            "fr"
          );
      }
    });

    return out;
  }, [clubsEnriched, clubSearch, selectedDepartement, sortBy]);

  const globalStats = useMemo(() => {
    const uniqueTeachers = uniqueByKey(
      groups.flatMap((group) => group?.enseignants || []),
      getTeacherKey
    );

    const totalAdherents = groups.reduce(
      (sum, group) => sum + Number(group?.adherentsCount || 0),
      0
    );

    return [
      {
        label: "Clubs",
        value: clubsEnriched.length,
        icon: FaBuilding,
        tone: "blue",
        hint: "Structures enregistrées",
      },
      {
        label: "Dojos",
        value: dojos.length,
        icon: FaMapMarkerAlt,
        tone: "emerald",
        hint: "Lieux d'entraînement",
      },
      {
        label: "Groupes",
        value: groups.length,
        icon: FaLayerGroup,
        tone: "violet",
        hint: "Créneaux / groupes",
      },
      {
        label: "Adhérents",
        value: totalAdherents,
        icon: FaUsers,
        tone: "amber",
        hint: "Total cumulé",
      },
      {
        label: "Enseignants",
        value: uniqueTeachers.length,
        icon: FaChalkboardTeacher,
        tone: "rose",
        hint: "Intervenants uniques",
      },
    ];
  }, [clubsEnriched, dojos, groups]);

  return (
    <div className="p-4 mt-[100px] w-full mx-auto font-satoshi">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="rounded-[28px] overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-500 px-6 py-7 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Groupes & Adhérents
                </h1>
                <p className="mt-2 text-sm md:text-base text-white/90 max-w-3xl">
                  Vue d’ensemble des clubs, dojos, groupes, enseignants et adhérents.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectedClubId ? (
                  <button
                    onClick={() => setSelectedClubId(null)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white/15 hover:bg-white/25 px-4 py-2.5 text-white transition"
                  >
                    <FaArrowLeft />
                    Retour aux clubs
                  </button>
                ) : null}

                <button
                  onClick={fetchData}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white/15 hover:bg-white/25 px-4 py-2.5 text-white transition"
                >
                  <FaSyncAlt className={cn(loading && "animate-spin")} />
                  Rafraîchir
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {!selectedClub ? (
              <StatsCards items={globalStats} />
            ) : (
              <StatsCards
                items={[
                  {
                    label: "Dojos",
                    value: selectedClub.totalDojos || 0,
                    icon: FaMapMarkerAlt,
                    tone: "emerald",
                    hint: "Lieux du club",
                  },
                  {
                    label: "Groupes",
                    value: selectedClub.totalGroups || 0,
                    icon: FaLayerGroup,
                    tone: "violet",
                    hint: "Créneaux rattachés",
                  },
                  {
                    label: "Adhérents",
                    value: selectedClub.totalAdherents || 0,
                    icon: FaUsers,
                    tone: "amber",
                    hint: "Total du club",
                  },
                  {
                    label: "Enseignants",
                    value: selectedClub.teachersCount || 0,
                    icon: FaChalkboardTeacher,
                    tone: "rose",
                    hint: "Intervenants uniques",
                  },
                ]}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-52 rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm animate-pulse"
              />
            ))}
          </div>
        ) : !selectedClub ? (
          <>
            <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <div className="lg:col-span-5">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Recherche club
                  </label>
                  <div className="mt-1 relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={clubSearch}
                      onChange={(e) => setClubSearch(e.target.value)}
                      placeholder="Nom du club, utilisateur, adresse..."
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-10 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
                    />
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Département
                  </label>
                  <select
                    value={selectedDepartement}
                    onChange={(e) => setSelectedDepartement(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
                  >
                    <option value="">Tous les départements</option>
                    {departementOptions.map((dep) => (
                      <option key={dep} value={dep}>
                        {dep}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Trier par
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
                  >
                    <option value="name">Nom du club</option>
                    <option value="departement">Département</option>
                    <option value="dojos_desc">Nb dojos</option>
                    <option value="groups_desc">Nb groupes</option>
                    <option value="adherents_desc">Nb adhérents</option>
                  </select>
                </div>

                <div className="lg:col-span-2 flex items-end">
                  <div className="w-full text-sm text-slate-500 dark:text-slate-400">
                    {filteredClubs.length} club
                    {filteredClubs.length > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </div>

            <ClubsGrid
              clubs={filteredClubs}
              onSelectClub={(clubId) => setSelectedClubId(clubId)}
            />
          </>
        ) : (
          <ClubDetailPanel
            club={selectedClub}
            adherentsByGroup={adherentsByGroup}
            loadingAdherents={loadingAdherents}
            onLoadAdherents={loadAdherentsForGroup}
          />
        )}
      </div>
    </div>
  );
};

export default GroupesAdherentsAdmin;